import React, { useState, ChangeEvent, useEffect } from "react";
import { toast } from "react-toastify";

import { LabelInput } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchElectricPlans,
  updateElectricPlan,
} from "../../../../features/auth/store/electricPlanSlice";
import Modal from "../../../../components/Reusable/Modal";
import { fetchElectricCategories } from "../../../../features/auth/store/electricCategorySlice";
import { fetchElectricProviders } from "../../../../features/auth/store/electricProviderSlice";
import { fetchCountries } from "../../../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../../../features/auth/store/currencySlice";

import { EditElectricPlan } from "../../../../features/auth/store/types/electric-types";

type EditElectricityProps = {
  isOpen: boolean;
  onClose?: () => void;
  electricPlan?: EditElectricPlan;
};

export const EditElectricModal: React.FC<EditElectricityProps> = ({
  isOpen,
  onClose,
  electricPlan,
}) => {
  const dispatch = useAppDispatch();

  // Providers and categories from redux
  const { electricProviders } = useAppSelector(
    (state) => state.electricProviders
  );
  const { electricCategories } = useAppSelector(
    (state) => state.electricCategories
  );

  const [formData, setFormData] = useState({
    provider_id: "",
    sub_category_id: "",
    disco: "",
    name: "",
    message: "",
    code: "",
    wallet_price: "",
    api_price: "",
    atm_price: "",
    status: "on",
    source: "",
    countries: [] as string[],
    discount: "",
    point_bonus: "",
    category: "",
    price_type: "fixed",
    price_currency_id: "",
    index: 1,
  });

  const [image, setImage] = useState<File | undefined>(undefined);
  const [currentImage, setcurrentImage] = useState<string | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);
  const countries = useAppSelector((state) => state.countries?.items ?? []);
  const countriesLoading = useAppSelector(
    (state) => state.countries?.loading ?? false
  );
  const currencies = useAppSelector((state) => state.currencies?.items ?? []);

  const deleteOldImage =
    electricPlan?.image_url !== null && currentImage === undefined;
  // Fetch providers on open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchElectricProviders());
      dispatch(fetchCountries({ perPage: 200 }));
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [dispatch, isOpen]);

  // Fetch categories when provider changes
  useEffect(() => {
    if (formData.provider_id) {
      dispatch(fetchElectricCategories());
    }
  }, [dispatch, formData.provider_id]);

  useEffect(() => {
    if (electricPlan) {
      let countries = [] as string[];
      if (electricPlan.countries) {
        const isString = electricPlan.countries.every(
          (country) => typeof country === "string"
        );
        countries = isString
          ? (electricPlan.countries as string[])
          : electricPlan.countries.map((country) => String(country));
      }

      setcurrentImage(electricPlan.image_url || "");

      setFormData({
        provider_id: electricPlan.category.provider_id.toString(),
        sub_category_id: electricPlan.category.id.toString(),
        wallet_price: electricPlan.wallet_price?.toString() || "",
        api_price: electricPlan?.api_price?.toString() || "",
        atm_price: electricPlan?.atm_price?.toString() || "",
        discount: electricPlan.discount?.toString() || "",
        point_bonus: electricPlan.point_bonus?.toString() || "",
        status: electricPlan.status,
        source: electricPlan.source,
        code: electricPlan.code || "",
        price_type: electricPlan.price_type || "fixed",
        price_currency_id: electricPlan.price_currency_id?.toString() || "",
        name: electricPlan.name || "",
        message: electricPlan.message,
        disco: electricPlan.disco,
        index: electricPlan.index || 1,
        category: electricPlan.category?.name,
        countries,
      });
    }
  }, [electricPlan]);

  // Filter categories for selected provider
  useEffect(() => {
    if (formData.provider_id && electricCategories.length) {
      setFilteredSubCategories(
        electricCategories.filter(
          (cat) => String(cat.provider_id) === String(formData.provider_id)
        )
      );
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.provider_id, electricCategories]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider_id" ? { sub_category_id: "" } : {}), // reset subcategory if provider changes
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const toggleCountry = (id: number) => {
    setFormData((prev) => {
      const exists = prev.countries.includes(String(id));
      return {
        ...prev,
        countries: exists
          ? prev.countries.filter((c) => c !== String(id))
          : [...prev.countries, String(id)],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!electricPlan) return;
    setIsSubmitting(true);

    // Basic validation
    if (!formData.disco || !formData.name || !formData.source) {
      toast.error("Please fill in all required fields");
      setIsSubmitting(false);
      return;
    }

    // if (isNaN(Number(formData.wallet_price)) {
    //   toast.error("Wallet price must be a valid number");
    //   setIsSubmitting(false);
    //   return;
    // }

    const formDataObj = new FormData();

    // Append all required fields
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("sub_category_id", formData.sub_category_id);
    formDataObj.append("category_id", formData.sub_category_id); // <-- Ensure category_id is sent
    formDataObj.append("disco", formData.disco);
    formDataObj.append("name", formData.name);
    formDataObj.append("message", formData.message || "");
    formDataObj.append("wallet_price", formData.wallet_price || "0");
    formDataObj.append("api_price", formData.api_price || "0");
    formDataObj.append("atm_price", formData.atm_price || "0");
    formDataObj.append("status", formData.status);
    formDataObj.append("source", formData.source);
    formDataObj.append("category", formData.category);
    formDataObj.append("discount", formData.discount);
    formDataObj.append("point_bonus", formData.point_bonus);
    formDataObj.append("code", formData.code || "");
    formDataObj.append(
      "countries",
      JSON.stringify(formData.countries.map((c) => Number(c)))
    );
    formDataObj.append("price_type", formData.price_type || "fixed");
    formDataObj.append("price_currency_id", formData.price_currency_id);
    formDataObj.append("index", String(formData.index || 1));

    if (image) {
      formDataObj.append("image", image);
    }
    if (deleteOldImage) {
      formDataObj.append("deleteOldImage", deleteOldImage.toString());
    }
    const filters: any = {};
    try {
      await dispatch(
        updateElectricPlan({ id: electricPlan.id, data: formDataObj })
      ).unwrap();
      toast.success("Electric plan edited successfully!");
      handleClose();
      await dispatch(fetchElectricPlans(filters)); // Refresh the list after creation
    } catch (error: any) {
      toast.error(error?.message || "Failed to edit electric plan");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    // Reset form
    setFormData({
      provider_id: "",
      sub_category_id: "",
      disco: "",
      name: "",
      message: "",
      wallet_price: "",
      api_price: "",
      atm_price: "",
      status: "on",
      source: "",
      countries: [],
      code: "",
      category: "",
      discount: "",
      point_bonus: "",
      price_type: "fixed",
      price_currency_id: "",
      index: 1,
    });
    setcurrentImage(undefined);
    setImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 w-[400px] max-h-[90vh] overflow-y-auto dark:bg-black dark:text-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-base font-semibold">Electric Plan Details</p>
          {/* Provider */}
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...electricProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          {/* Sub Category */}
          <FloatingLabelSelect
            name="sub_category_id"
            placeholder="Sub Category"
            value={formData.sub_category_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Sub Category" },
              ...filteredSubCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            disabled={!formData.provider_id}
          />
          {/* Status */}
          <div className="space-y-2">
            <FloatingLabelSelect
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              placeholder="Status"
              options={[
                { value: "on", label: "Active" },
                { value: "off", label: "Inactive" },
              ]}
            />
          </div>
          {/* Disco */}
          <div className="space-y-2">
            <FloatingLabelSelect
              name="disco"
              placeholder="Disco"
              value={formData.disco}
              onChange={handleInputChange}
              options={[
                { value: "IKEDC", label: "IKEDC" },
                { value: "IBEDC", label: "IBEDC" },
                { value: "KAEDCO", label: "KAEDCO" },
                { value: "AEDC", label: "AEDC" },
                { value: "PHED", label: "PHED" },
                { value: "JED", label: "JED" },
                { value: "EKEDC", label: "EKEDC" },
                { value: "EEDC", label: "EEDC" },
                { value: "BEDC", label: "BEDC" },
              ]}
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="name"
              placeholder="Plan Name"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>
          {/* Product Category */}
          <div>
            <FloatingLabelSelect
              name="category"
              placeholder="Product Category"
              value={formData.category}
              onChange={handleInputChange}
              options={[
                { value: "prePaid", label: "Prepaid" },
                { value: "postPaid", label: "Postpaid" },
              ]}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="wallet_price"
              placeholder="Wallet Price"
              value={formData.wallet_price}
              onChange={handleInputChange}
            />
          </div>
          {/* ATM Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="atm_price"
              placeholder="ATM Price"
              value={formData.atm_price}
              onChange={handleInputChange}
            />
          </div>
          {/* API Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="api_price"
              placeholder="API Price"
              value={formData.api_price}
              onChange={handleInputChange}
            />
          </div>
          {/* Discount Offered  */}
          <LabelInput
            type="number"
            name="discount"
            placeholder="Discount Offered"
            value={formData.discount}
            onChange={handleInputChange}
          />
          <LabelInput
            type="number"
            name="point_bonus"
            placeholder="Point Bonus"
            value={formData.point_bonus}
            onChange={handleInputChange}
          />
          {/* Price Type */}
          <FloatingLabelSelect
            name="price_type"
            placeholder="Price Type"
            value={formData.price_type}
            onChange={handleInputChange}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "percent", label: "Percent" },
            ]}
          />
          {/* Price Currency */}
          <FloatingLabelSelect
            name="price_currency_id"
            placeholder="Price Currency"
            value={formData.price_currency_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Currency" },
              ...currencies.map((c) => ({
                value: String(c.id),
                label: `${c.name} (${c.symbol})`,
              })),
            ]}
          />
          {/* Source */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="source"
              placeholder="Source"
              value={formData.source}
              onChange={handleInputChange}
            />
          </div>

          {/* Code */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="code"
              placeholder="Code (Optional)"
              value={formData.code || ""}
              onChange={handleInputChange}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <p className="text-sm font-medium mb-1">Description</p>
            <textarea
              name="message"
              placeholder="Description"
              value={formData.message}
              onChange={handleInputChange}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500  dark:bg-white/10 dark:text-white"
            />
          </div>

          {/* Countries */}
          <h3 className="text-lg font-semibold text-gray-800 mb-2 dark:text-white">
            Countries (availability)
          </h3>
          <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
            {countriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : countries.length === 0 ? (
              <p className="text-sm text-gray-500">
                No countries found. Create countries first.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {countries.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(String(c.id))}
                      onChange={() => toggleCountry(c.id)}
                    />
                    <span>
                      {c.name} ({c.symbol})
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Product Images */}
          <div className="space-y-4">
            <p className="text-base font-semibold">Product Images</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center  dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
                multiple
              />
              <label
                htmlFor="fileUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  Upload from computer
                </span>
              </label>
            </div>
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="product"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage()}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {currentImage && (
              <div>
                <p className="text-base font-semibold">Current Product Image</p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20">
                    <img
                      src={currentImage}
                      alt="product"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setcurrentImage(undefined)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              onClick={handleClose}
              bgColor="bg-gray-300 hover:bg-gray-400"
              textColor="text-gray-700"
              borderRadius="rounded-lg"
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              bgColor={
                isSubmitting
                  ? "bg-gray-300"
                  : "bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              }
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-[103px] h-10"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
